import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Helmet } from "react-helmet-async";

import { FloatingInstallButton } from "@/client/components/install-app-button";
import { STYLINGCV_SAME_AS } from "@/client/constants/social-links";
import { useFormFocus } from "@/client/contexts/form-focus-context";

import { AgentSquadSection } from "./sections/agent-squad";
import { AtmosphericHeroSection } from "./sections/atmospheric-hero";
import { ATSOptimizationSection } from "./sections/ats-optimization";
import { EmployersMarqueeSection } from "./sections/employers-marquee";
import { FAQSection } from "./sections/faq";
import { FinalCTASection } from "./sections/final-cta";
import { MoreReasonsSection } from "./sections/more-reasons";
import { PricingTeaserSection } from "./sections/pricing-teaser";
import { SocialProofSection } from "./sections/social-proof";
import { SupportSection } from "./sections/support";
import { TemplatesSection } from "./sections/templates";
import { TrustBarSection } from "./sections/trust-bar";

export const HomePage = () => {
  const { i18n } = useLingui();
  const { isFormFocused } = useFormFocus();

  return (
    <main className="home-page relative isolate overflow-x-clip bg-[#050505] min-h-screen">
      <Helmet prioritizeSeoTags>
        <html lang={i18n.locale} />

        <title>
          {t`Stylion`} - {t`Ai Resume Generator/Builder`}
        </title>

        <meta
          name="description"
          content={t`Stylion Resume Generator/Builder that simplifies the process of creating, updating, and sharing your resume.`}
        />

        {/* Structured Data for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Stylion - AI Resume Builder",
            applicationCategory: "BusinessApplication",
            applicationSubCategory: "Resume Builder",
            operatingSystem: "Web Browser",
            url: "https://ai.stylion.io",
            description:
              "Professional AI-powered resume builder with ATS optimization, 15+ languages support, and 98% success rate. Create stunning resumes in minutes with our smart generator.",
            softwareVersion: "2.6.5",
            datePublished: "2023-01-01",
            dateModified: new Date().toISOString().split("T")[0],
            author: {
              "@type": "Organization",
              name: "Stylion",
              url: "https://ai.stylion.io",
            },
            publisher: {
              "@type": "Organization",
              name: "Stylion",
              url: "https://ai.stylion.io",
              logo: {
                "@type": "ImageObject",
                url: "https://ai.stylion.io/logo.png",
              },
            },
            offers: [
              {
                "@type": "Offer",
                name: "Free Plan",
                price: "0",
                priceCurrency: "USD",
                description: "Basic resume creation with limited features",
              },
              {
                "@type": "Offer",
                name: "Basic Plan",
                price: "8.99",
                priceCurrency: "USD",
                billingDuration: "P1Y",
                description: "Essential features with 500 monthly tokens",
              },
              {
                "@type": "Offer",
                name: "Professional Plan",
                price: "8.99",
                priceCurrency: "USD",
                billingDuration: "P1Y",
                description: "All premium features with 2000 monthly tokens",
              },
            ],
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              bestRating: "5",
              worstRating: "1",
              ratingCount: "12847",
              reviewCount: "8934",
            },
            featureList: [
              "AI-Powered Resume Generation",
              "ATS Optimization (98% Success Rate)",
              "15+ Language Support including Arabic",
              "Professional Templates",
              "Real-time Preview",
              "PDF Export",
              "Industry-Specific Keywords",
              "Secure & Private Data Handling",
              "Mobile Responsive Design",
              t`One-Click LinkedIn Import`,
            ],
            screenshot: "https://ai.stylion.io/screenshot.png",
            softwareHelp: {
              "@type": "CreativeWork",
              url: "https://ai.stylion.io/help",
            },
            installUrl: "https://ai.stylion.io/ai-generator",
            downloadUrl: "https://ai.stylion.io",
            supportingData: {
              "@type": "DataSet",
              name: t`Resume Success Statistics`,
              description: "Over 1.8M resumes created with 98% ATS pass rate",
            },
          })}
        </script>

        {/* Additional Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Stylion",
            url: "https://ai.stylion.io",
            logo: "https://ai.stylion.io/logo.png",
            description:
              "Leading AI-powered resume builder helping professionals create ATS-optimized resumes",
            foundingDate: "2023",
            sameAs: STYLINGCV_SAME_AS,
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer service",
              url: "https://ai.stylion.io/support",
            },
          })}
        </script>

        {/* WebSite Schema for Search Box */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Stylion - AI Resume Builder",
            url: "https://ai.stylion.io",
            description: "Professional AI resume builder with ATS optimization",
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: "https://ai.stylion.io/templates?search={search_term_string}",
              },
              "query-input": "required name=search_term_string",
            },
          })}
        </script>
      </Helmet>

      {/* Hero — inline AI resume generator */}
      <AtmosphericHeroSection />

      {!isFormFocused && (
        <>
          <TrustBarSection />
          <EmployersMarqueeSection />
          <MoreReasonsSection />
          <ATSOptimizationSection />
          <AgentSquadSection />
          <TemplatesSection />
          <SocialProofSection />
          <PricingTeaserSection />
          <FAQSection />
          <SupportSection />
          <FinalCTASection />
        </>
      )}

      {/* Floating PWA Install Button */}
      <FloatingInstallButton delay={10000} position="bottom-right" />
    </main>
  );
};
